/**
 * TimelineYAxis Component
 *
 * Y-axis labels section with crop chips and group headers.
 * Synchronized vertical scroll with content area.
 */

import React from 'react'
import { Box } from '@mui/material'
import {
  useTimelineContextDispatch,
  setExpandedIds as setExpandedIdsAction,
} from '../context/timeline-context'
import { useTimelineStaticContext } from '../context/timeline-static-context'
import { useOrgHoverContext } from '~/common/context/organization/org-context'
import {
  X_AXIS_HEIGHT,
  VERTICAL_DAY_TRACKER_SPACE,
  GROUP_GAP,
  SELECTOR_BOTTOM_PADDING,
} from '../timeline-constants'
import { TimelineGroupHeader } from './y-axis/timeline-group-header'
import { TimelineCropRow } from './y-axis/timeline-crop-row'

/**
 * TimelineYAxis - Crop label chips with group headers
 *
 * Features:
 * - Synchronized vertical scroll with content
 * - Group headers with expand/collapse
 * - Individual crop label chips
 * - Hover actions (edit/delete buttons on expanded chips)
 *
 * @param {Object} props
 * @param {Array} props.groups - Grouped crops array
 * @param {boolean} props.hideYAxis - Hide Y-axis flag
 * @param {Array} props.expandedGroupIds - Expanded group IDs
 * @param {Function} props.toggleGroup - Group toggle handler
 */
export const TimelineYAxis = ({ groups, hideYAxis, expandedGroupIds, toggleGroup }) => {
  const {
    expandedIds,
    yAxisRef,
    totalRowsHeight,
  } = useTimelineStaticContext()

  const dispatch = useTimelineContextDispatch()
  const { hoveredOrg } = useOrgHoverContext()

  if (hideYAxis) return null

  return (
    <Box
      sx={{
        position: 'absolute',
        top: X_AXIS_HEIGHT,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 30,
      }}
    >
      {/* Y-axis labels container (scrolls vertically with content) */}
      <Box
        ref={yAxisRef}
        sx={{
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            pt: VERTICAL_DAY_TRACKER_SPACE,
            minHeight: totalRowsHeight + 2 * SELECTOR_BOTTOM_PADDING,
            flex: '0 0 auto',
            zIndex: 6
          }}
        >
          {groups.map((group, groupIdx) => {
            const isGroupExpanded = expandedGroupIds.includes(group.groupId)

            return (
              <React.Fragment key={group.groupId}>
                {/* Group gap before each group (except first) */}
                {groupIdx > 0 && <Box sx={{ height: GROUP_GAP }} />}

                {/* Group header - only show for actual groups, not ungrouped items */}
                {!group.isUngrouped && (
                  <TimelineGroupHeader
                    group={group}
                    isExpanded={isGroupExpanded}
                    onToggle={() => {
                      // Toggle group expansion
                      toggleGroup(group.groupId)

                      // When expanding group, also expand all crops in that group
                      if (!isGroupExpanded) {
                        const newExpanded = [...expandedIds]
                        group.crops.forEach(crop => {
                          if (!newExpanded.includes(crop.id)) {
                            newExpanded.push(crop.id)
                          }
                        })
                        dispatch(setExpandedIdsAction({ expandedIds: newExpanded }))
                      } else {
                        // When collapsing group, collapse all crops in that group
                        const cropIdsInGroup = group.crops.map(c => c.id)
                        const newExpandedIds = expandedIds.filter(id => !cropIdsInGroup.includes(id))
                        dispatch(setExpandedIdsAction({ expandedIds: newExpandedIds }))
                      }
                    }}
                  />
                )}

                {/* Individual crop chips */}
                {group.crops.map((crop) => {
                  const isExpanded = expandedIds.includes(crop.id)
                  const isParentOrgHovered = crop.organization_id === hoveredOrg?.id

                  return (
                    <TimelineCropRow
                      key={crop.id}
                      crop={crop}
                      isExpanded={isExpanded}
                      isParentOrgHovered={isParentOrgHovered}
                      onToggle={() => {
                        const newExpandedIds = expandedIds.includes(crop.id)
                          ? expandedIds.filter(i => i !== crop.id)
                          : [...expandedIds, crop.id]
                        dispatch(setExpandedIdsAction({ expandedIds: newExpandedIds }))
                      }}
                    />
                  )
                })}
              </React.Fragment>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}
